import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/services/chauffeur-services.png";
import { cityPriceFaq } from "./cityPricing";

const saintPaulDeVence: ChauffeurPage = {
  slug: "saint-paul-de-vence",
  title: "Chauffeur privé à Saint-Paul-de-Vence – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Saint-Paul-de-Vence : dépose au pied des remparts, Fondation Maeght, dîner à La Colombe d’Or et transfert aéroport de Nice en ~30 min.",
  keywords: [
    "chauffeur privé saint-paul-de-vence",
    "vtc saint-paul-de-vence",
    "chauffeur fondation maeght",
    "transfert saint-paul-de-vence aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Saint-Paul-de-Vence",
    subtitle:
      "Le village d’art perché, ses galeries et ses remparts : un chauffeur qui vous laisse au plus près des ruelles piétonnes, sans chercher à se garer.",
    keywords: [
      "VTC Saint-Paul-de-Vence",
      "chauffeur Fondation Maeght",
      "transfert aéroport Nice",
      "chauffeur village d’art",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Saint-Paul-de-Vence",
    badges: ["Village piéton", "Fondation Maeght", "~30 min de l’aéroport"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Saint-Paul-de-Vence",
    items: [
      {
        title: "Dépose au pied des remparts",
        description:
          "Le cœur médiéval est entièrement piéton et le stationnement rare à ses abords : le chauffeur vous laisse à l’entrée du village et revient sur simple appel, sans tourner pour une place.",
      },
      {
        title: "Village d’art & galeries",
        description:
          "De la Fondation Maeght à la rue Grande et ses ateliers, jusqu’au cimetière où repose Chagall : une journée d’art enchaînée sans souci de logistique.",
      },
      {
        title: "Dîner à La Colombe d’Or",
        description:
          "Aller et retour pour la table mythique du village et sa collection de peintures : vous profitez du dîner, le chauffeur gère le trajet de nuit vers votre hôtel.",
      },
      {
        title: "À ~30 minutes de l’aéroport",
        description:
          "Transfert direct depuis et vers l’aéroport de Nice Côte d’Azur, via l’A8 et Cagnes-sur-Mer, avec suivi de vol et accueil personnalisé.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Saint-Paul-de-Vence",
    services: [
      {
        title: "Visite du village perché",
        text: "Dépose à l’entrée des remparts, attente pendant la flânerie rue Grande et reprise à l’heure convenue, sans contrainte de parking.",
      },
      {
        title: "Fondation Maeght & galeries",
        text: "Accès à la Fondation Maeght et aux galeries d’art, avec temps d’attente inclus pour visiter à votre rythme.",
      },
      {
        title: "Tables du village",
        text: "La Colombe d’Or, Le Saint-Paul et les restaurants alentour : aller-retour de soirée en toute sérénité, sans reprendre le volant.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Liaison Saint-Paul-de-Vence ⇄ aéroport de Nice en ~30 min, avec suivi de vol et accueil à l’arrivée.",
      },
      {
        title: "Mise à disposition & arrière-pays",
        text: "À l’heure ou à la journée pour combiner Saint-Paul, Vence, La Colle-sur-Loup et les Gorges du Loup avec un chauffeur dédié.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Entrée des remparts",
        description:
          "Porte de Vence et parkings d’accès, au pied du village piéton.",
      },
      {
        name: "Rue Grande",
        description:
          "Ruelle centrale bordée de galeries et d’ateliers d’artistes.",
      },
      {
        name: "Fondation Maeght",
        description:
          "Musée d’art moderne et ses jardins de sculptures, hors les murs.",
      },
      {
        name: "La Colombe d’Or",
        description: "Hôtel-restaurant historique et sa collection d’œuvres.",
      },
      {
        name: "Cimetière du village",
        description: "Sépulture de Marc Chagall, en surplomb des remparts.",
      },
      {
        name: "La Colle-sur-Loup",
        description:
          "Commune voisine, sur la route de l’arrière-pays et des Gorges du Loup.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur pour le village d’art",
    subheading:
      "Saint-Paul-de-Vence se mérite à pied ; l’approche et le retour, eux, se gèrent au volant.",
    paragraphs: [
      "Perché sur son éperon entre Nice et Vence, Saint-Paul-de-Vence se visite à pied, à l’intérieur de remparts fermés à la circulation. Aux abords, les places sont rares en saison : le chauffeur vous dépose au plus près de la porte du village et vous récupère sur appel, sans que vous ayez à composer avec le stationnement.",
      "KS Rent Services fonctionne en forfaits fixes annoncés à l’avance — par exemple Saint-Paul-de-Vence ⇄ aéroport de Nice — et en mise à disposition à l’heure ou à la journée, avec devis immédiat. Nos chauffeurs bilingues français/anglais, basés sur la Côte d’Azur, sont notés 5,0/5 sur 38 avis Google.",
      "Une même journée peut enchaîner la Fondation Maeght, un déjeuner rue Grande et une escapade vers La Colle-sur-Loup ou les Gorges du Loup : un chauffeur dédié garde le fil du programme et adapte les horaires à votre visite.",
    ],
    bullets: [
      "Dépose au pied des remparts, reprise sur appel",
      "Forfaits fixes & mise à disposition, devis immédiat",
      "Chauffeurs bilingues FR/EN, 5,0/5 sur 38 avis Google",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Saint-Paul-de-Vence",
    steps: [
      "Indiquez votre programme : village, Fondation Maeght, restaurant ou transfert aéroport.",
      "Nous confirmons le véhicule adapté et le forfait ou la mise à disposition.",
      "Le chauffeur vous dépose à l’entrée des remparts ou à l’adresse voulue.",
      "Visite ou dîner à votre rythme, reprise sur appel pour la suite.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Saint-Paul-de-Vence"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 30 minutes entre Saint-Paul-de-Vence et l’aéroport de Nice Côte d’Azur, via Cagnes-sur-Mer et l’A8. Le chauffeur suit votre vol et ajuste l’heure de départ en conséquence.",
    },
    {
      q: "Peut-on entrer dans le village en voiture ?",
      a: "Le cœur de Saint-Paul-de-Vence est entièrement piéton. Le chauffeur vous dépose au plus près de la porte des remparts, puis vous reprend au même endroit une fois la visite terminée.",
    },
    {
      q: "Pouvez-vous nous conduire à la Fondation Maeght et attendre ?",
      a: "Oui. Nous vous déposons à la Fondation Maeght, hors les murs du village, avec temps d’attente inclus pour parcourir les collections et les jardins de sculptures à votre rythme.",
    },
    {
      q: "Assurez-vous le retour après un dîner à La Colombe d’Or ?",
      a: "Oui, l’aller comme le retour de nuit sont couverts. Vous profitez du dîner sans reprendre le volant, et le chauffeur vous ramène à votre hôtel à l’heure convenue.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "En été et lors des expositions, réservez au plus tôt pour garantir le véhicule ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale des galeries et hôtels de Saint-Paul-de-Vence.",
    },
    {
      q: "Quels véhicules proposez-vous pour Saint-Paul-de-Vence ?",
      a: "Berline Mercedes, van ou SUV selon le nombre de passagers, adaptés aux routes étroites qui mènent au village et à ses abords.",
    },
  ],
  relatedLinks: [
    {
      label: "Aéroport de Nice, tarifs et trajets",
      href: "/chauffeur/aeroport",
    },
    { label: "Réserver un chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Service de chauffeur sur la Riviera",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Mise à disposition avec chauffeur à Cannes",
      href: "/chauffeur/cannes",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Saint-Paul-de-Vence",
    text: "Visite du village, Fondation Maeght ou transfert aéroport : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Saint-Paul-de-Vence — VTC 24h/24",
    description:
      "Chauffeur privé et VTC à Saint-Paul-de-Vence : village d’art, Fondation Maeght, La Colombe d’Or et transfert aéroport de Nice en ~30 min. 5,0/5 sur 38 avis.",
    canonical: "/chauffeur/saint-paul-de-vence",
  },
};

export default saintPaulDeVence;
